import React, { useEffect, useRef, useState } from 'react';

/**
 * Snowfall Animation Component
 * Renders falling snowflakes using canvas for optimal performance
 * Auto-disables after Jan 2, 2026
 */
const Snowfall = ({ enabled = true }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Check if we're past the cutoff date (Jan 2, 2026 00:00 local time)
    const cutoffDate = new Date('2026-01-02T00:00:00');
    const now = new Date();
    
    if (now >= cutoffDate || !enabled) {
      setIsActive(false);
      return;
    }
    
    setIsActive(true);
  }, [enabled]);

  useEffect(() => {
    if (!isActive || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Snowflake particles
    const snowflakes = [];
    const maxSnowflakes = window.innerWidth < 768 ? 50 : 100; // Fewer on mobile

    class Snowflake {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * -canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speed = Math.random() * 1 + 0.5;
        this.opacity = Math.random() * 0.5 + 0.2;
        this.wind = Math.random() * 0.5 - 0.25;
        this.swing = Math.random() * 2;
        this.swingSpeed = Math.random() * 0.02 + 0.01;
        this.angle = Math.random() * Math.PI * 2;
      }

      update() {
        this.y += this.speed;
        this.angle += this.swingSpeed;
        this.x += Math.sin(this.angle) * this.swing + this.wind;

        // Reset if out of bounds
        if (this.y > canvas.height + 10 || this.x < -10 || this.x > canvas.width + 10) {
          this.reset();
          this.y = -10;
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`;
        ctx.fill();
      }
    }

    // Initialize snowflakes
    for (let i = 0; i < maxSnowflakes; i++) {
      const flake = new Snowflake();
      flake.y = Math.random() * canvas.height; // Start distributed
      snowflakes.push(flake);
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      snowflakes.forEach(flake => {
        flake.update();
        flake.draw();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9998, // Below modals (9999) but above most content
      }}
      aria-hidden="true"
    />
  );
};

export default Snowfall;
